import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Database,
  HardDrive,
  History as HistoryIcon,
  ShieldAlert,
  Workflow,
} from "lucide-react";
import { api, type ProjectDatabaseSnapshot, type Schema } from "@/lib/api";
import {
  ErrorBox,
  PageHeader,
  Spinner,
  SuccessBox,
  WarnBox,
} from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/** Map operation / record state strings to Badge variants for scannable UI. */
export function stateBadgeVariant(
  state: string,
): "success" | "warning" | "danger" | "secondary" | "default" {
  const s = state.toLowerCase();
  if (
    s === "active" ||
    s === "ready" ||
    s === "succeeded" ||
    s === "completed" ||
    s === "success"
  ) {
    return "success";
  }
  if (
    s === "failed" ||
    s === "error" ||
    s === "purged" ||
    s === "quarantined"
  ) {
    return "danger";
  }
  if (
    s === "running" ||
    s === "queued" ||
    s === "pending" ||
    s === "provisioning" ||
    s === "suspended" ||
    s === "sleeping" ||
    s === "observing" ||
    s === "in_progress"
  ) {
    return "warning";
  }
  return "secondary";
}

/** Human-readable kind label for operation rows. */
export function formatOperationKind(kind: string): string {
  return kind.replace(/_/g, " ");
}

export type HistoryRowModel = {
  id: string;
  kind: string;
  state: string;
  detail: string;
};

/** Present a project database operation as a scannable history row. */
export function operationToHistoryRow(
  o: ProjectDatabaseSnapshot["operations"][number],
): HistoryRowModel {
  return {
    id: o.operation_id,
    kind: o.kind,
    state: o.state,
    detail: `${o.step} · attempt ${o.attempts}${o.error_code ? ` · ${o.error_code}` : ""}`,
  };
}

export function backupToHistoryRow(
  b: ProjectDatabaseSnapshot["backups"][number],
): HistoryRowModel {
  return {
    id: b.backup_id,
    kind: b.kind,
    state: b.state,
    detail: `${b.external_target ? "external" : "NON-DR"}${b.error_code ? ` · ${b.error_code}` : ""}`,
  };
}

export function migrationToHistoryRow(
  m: ProjectDatabaseSnapshot["legacy_migrations"][number],
): HistoryRowModel {
  return {
    id: m.migration_id,
    kind: `generation ${m.source_generation} → ${m.target_generation}`,
    state: m.state,
    detail:
      m.error_code ??
      (m.rollback_deadline
        ? `rollback until ${new Date(m.rollback_deadline).toLocaleString()}`
        : ""),
  };
}

const UUID_CONFIRM_KINDS = new Set([
  "purge",
  "restore_backup",
  "finalize_legacy",
]);

type PendingLifecycleAction = {
  channel: "action";
  kind: string;
  extra: Record<string, unknown>;
  requiresUUID: boolean;
};

type PendingCatalogAction = {
  channel: "catalog";
  body: Record<string, unknown>;
  label: string;
};

type PendingAction = PendingLifecycleAction | PendingCatalogAction;

export function DatabaseManagementPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [snapshot, setSnapshot] = useState<ProjectDatabaseSnapshot | null>(
    null,
  );
  const [catalog, setCatalog] = useState<Schema | null>(null);
  const [catalogError, setCatalogError] = useState<unknown>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState("");
  const [pending, setPending] = useState<PendingAction | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const status = await api.getProjectDatabase(id);
      setSnapshot(status.data);
      setError(null);
      const observed = status.data.placement.observed_state;
      if (observed !== "active" && observed !== "sleeping") {
        setCatalog(null);
        setCatalogError(null);
        return;
      }
      try {
        const schema = await api.getSchema(id);
        setCatalog(schema.data);
        setCatalogError(null);
      } catch (e) {
        setCatalog(null);
        setCatalogError(e);
      }
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load]);

  const requestAction = (
    kind: string,
    extra: Record<string, unknown> = {},
  ) => {
    setError(null);
    setSuccess("");
    setPending({
      channel: "action",
      kind,
      extra,
      requiresUUID: UUID_CONFIRM_KINDS.has(kind),
    });
  };

  const runWithPassword = async (
    password: string,
    confirmation: string,
  ) => {
    if (!pending || !id) return;
    setBusy(true);
    setError(null);
    setSuccess("");
    try {
      if (pending.channel === "action") {
        if (pending.requiresUUID && confirmation !== id) {
          setError(
            new Error(
              "Type the exact Project UUID to confirm this destructive action.",
            ),
          );
          return;
        }
        await api.projectDatabaseAction(id, {
          kind: pending.kind,
          reason: `Dashboard ${pending.kind}`,
          current_password: password,
          ...(pending.requiresUUID ? { confirmation } : {}),
          ...pending.extra,
        });
        setSuccess(`${pending.kind} queued`);
      } else {
        await api.manageProjectDatabase(id, {
          ...pending.body,
          current_password: password,
        });
        setSuccess("Database operation queued");
      }
      setPending(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (!snapshot && !error) {
    return <Spinner label="Loading database control…" />;
  }

  const p = snapshot?.placement;
  const latestMigration = snapshot?.legacy_migrations[0];
  const readyPhysical = snapshot?.backups.filter(
    (b) => b.kind === "physical_base" && b.state === "ready",
  )[0];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Operate"
        title="Project database"
        description="Dedicated PostgreSQL cluster lifecycle, catalog operations, external backup/PITR and legacy migration."
      />

      <WarnBox>
        Single physical node: this deployment is <strong>not HA</strong>.
        Same-node replicas and same-node backup copies do not satisfy disaster
        recovery. Every control action requires your current owner password.
      </WarnBox>

      {error ? <ErrorBox error={error} /> : null}
      {success ? <SuccessBox>{success}</SuccessBox> : null}

      <ReauthDialog
        projectId={id}
        pending={pending}
        busy={busy}
        onCancel={() => {
          if (!busy) setPending(null);
        }}
        onConfirm={(password, confirmation) =>
          void runWithPassword(password, confirmation)
        }
      />

      {snapshot && p ? (
        <>
          {/* Placement / lifecycle */}
          <Card data-testid="section-placement">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-sm border border-border-standard bg-surface-container-low text-secondary">
                    <Database className="h-4 w-4" aria-hidden />
                  </div>
                  <div>
                    <CardTitle>Placement</CardTitle>
                    <CardDescription>
                      Cluster lifecycle and desired vs observed state.
                    </CardDescription>
                  </div>
                </div>
                <Badge
                  variant={stateBadgeVariant(p.observed_state)}
                  data-testid="placement-state-badge"
                >
                  {p.observed_state}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Metric label="Mode" value={p.mode} />
                <Metric
                  label="State"
                  value={`${p.observed_state} → ${p.desired_state}`}
                />
                <Metric label="Generation" value={String(p.generation)} />
                <Metric label="Topology" value="1 node · not HA" />
               </div>
               <div
                 className="border-t border-border-standard pt-4"
                 data-testid="section-resource-budget"
               >
                 <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-muted">
                   Resource budget
                 </div>
                 <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                   <Metric label="CPU" value={budgetValue(p.resource_profile, "cpu_cores", " core")} />
                   <Metric label="Memory" value={budgetValue(p.resource_profile, "memory_mb", " MB")} />
                   <Metric label="Connections" value={budgetValue(p.resource_profile, "max_connections", "")} />
                   <Metric label="Disk" value={budgetValue(p.resource_profile, "disk_mb", " MB")} />
                   <Metric label="WAL" value={budgetValue(p.resource_profile, "wal_mb", " MB")} />
                 </div>
                 <p className="mt-3 text-xs text-text-muted">
                   Limits are enforced per Project; the host admission guard
                   rejects capacity overcommit and quarantines hard usage-limit
                   violations.
                 </p>
               </div>
               <div
                className="flex flex-wrap gap-2 border-t border-border-standard pt-4"
                role="group"
                aria-label="Lifecycle actions"
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy || !!pending}
                  onClick={() => requestAction(
                    p.observed_state === "suspended" || p.observed_state === "sleeping"
                      ? "resume"
                      : "suspend",
                  )}
                >
                  {p.observed_state === "sleeping"
                    ? "Wake database"
                    : p.observed_state === "suspended"
                      ? "Resume"
                      : "Suspend"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy || !!pending}
                  onClick={() => requestAction("quarantine")}
                >
                  Quarantine
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={busy || !!pending}
                  onClick={() => requestAction("purge")}
                >
                  Purge exact Project
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Catalog — only when active */}
          {p.observed_state === "active" ? (
            <>
              <CatalogOperations
                catalog={catalog}
                busy={busy || !!pending}
                onQueue={(body, label) => {
                  setError(null);
                  setSuccess("");
                  setPending({ channel: "catalog", body, label });
                }}
              />
              {catalogError ? <ErrorBox error={catalogError} /> : null}
            </>
          ) : (
            <div data-testid="catalog-inactive-warning">
              <WarnBox>
                {p.observed_state === "sleeping" ? (
                  <>Project database is sleeping to preserve host resources. Opening catalog access will wake its dedicated database automatically.</>
                ) : (
                  <>Project database is <strong>{p.observed_state}</strong>. Catalog access will become available after provisioning reaches active state.</>
                )}
              </WarnBox>
            </div>
          )}

          {/* Backup / PITR */}
          <Card data-testid="section-backup">
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-sm border border-border-standard bg-surface-container-low text-secondary">
                  <HardDrive className="h-4 w-4" aria-hidden />
                </div>
                <div>
                  <CardTitle>External backup and PITR</CardTitle>
                  <CardDescription>
                    Physical base backups, logical exports, and restore to a new
                    generation.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!snapshot.capabilities.external_backup ? (
                <p className="text-sm text-text-muted">
                  Disabled until an external failure-domain repository is
                  explicitly configured. Same-node storage is rejected.
                </p>
              ) : (
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-label="Backup actions"
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy || !!pending}
                    onClick={() => requestAction("backup_physical")}
                  >
                    Physical base backup
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy || !!pending}
                    onClick={() => requestAction("backup_logical")}
                  >
                    Logical export + manifest
                  </Button>
                </div>
              )}
              <OperationHistory
                title="Backup history"
                empty="No backups yet."
                rows={snapshot.backups.map(backupToHistoryRow)}
              />
              {snapshot.capabilities.pitr_restore && readyPhysical ? (
                <div
                  className="flex flex-wrap gap-2 border-t border-border-standard pt-4"
                  role="group"
                  aria-label="PITR actions"
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy || !!pending}
                    onClick={() =>
                      requestAction("archive_wal", {
                        backup_id: readyPhysical.backup_id,
                      })
                    }
                  >
                    Archive WAL
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={busy || !!pending}
                    onClick={() =>
                      requestAction("restore_backup", {
                        backup_id: readyPhysical.backup_id,
                        target_time: new Date().toISOString(),
                      })
                    }
                  >
                    Restore as new generation
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Legacy migration */}
          <Card data-testid="section-legacy-migration">
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-sm border border-border-standard bg-surface-container-low text-secondary">
                  <Workflow className="h-4 w-4" aria-hidden />
                </div>
                <div>
                  <CardTitle>Legacy migration</CardTitle>
                  <CardDescription>
                    Validated copy, short write freeze, atomic cutover and
                    bounded rollback. No steady dual write.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Legacy migration actions"
              >
                {p.mode === "legacy_schema" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy || !!pending}
                    onClick={() => requestAction("migrate_legacy")}
                  >
                    Migrate to isolated cluster
                  </Button>
                ) : null}
                {latestMigration?.state === "observing" ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy || !!pending}
                      onClick={() =>
                        requestAction("rollback_legacy", {
                          migration_id: latestMigration.migration_id,
                        })
                      }
                    >
                      Rollback to legacy
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={busy || !!pending}
                      onClick={() =>
                        requestAction("finalize_legacy", {
                          migration_id: latestMigration.migration_id,
                        })
                      }
                    >
                      Finalize and purge legacy
                    </Button>
                  </>
                ) : null}
              </div>
              <OperationHistory
                title="Migration history"
                empty="No migrations recorded."
                rows={snapshot.legacy_migrations.map(migrationToHistoryRow)}
              />
            </CardContent>
          </Card>

          {/* Recent operations */}
          <Card data-testid="section-operations">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-sm border border-border-standard bg-surface-container-low text-secondary">
                    <HistoryIcon className="h-4 w-4" aria-hidden />
                  </div>
                  <div>
                    <CardTitle>Recent operations</CardTitle>
                    <CardDescription>
                      Queued and completed control-plane operations for this
                      project database.
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="secondary">
                  {snapshot.operations.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <OperationHistory
                empty="No operations yet."
                rows={snapshot.operations.map(operationToHistoryRow)}
                emphasize
              />
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function ReauthDialog({
  projectId,
  pending,
  busy,
  onCancel,
  onConfirm,
}: {
  projectId: string;
  pending: PendingAction | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (password: string, confirmation: string) => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const open = pending !== null;
  const requiresUUID =
    pending?.channel === "action" ? pending.requiresUUID : false;
  const title = !pending
    ? "Confirm action"
    : pending.channel === "action"
      ? `Confirm ${formatOperationKind(pending.kind)}`
      : `Confirm ${pending.label}`;
  const canSubmit =
    !busy &&
    password.length > 0 &&
    (!requiresUUID || confirmation === projectId);

  // Reset fields whenever a new action is staged.
  useEffect(() => {
    if (pending) {
      setPassword("");
      setConfirmation("");
    }
  }, [pending]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogContent
        data-testid="reauth-panel"
        onPointerDownOutside={(event) => {
          if (busy) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <DialogHeader>
          <div className="flex items-start gap-3 pr-6">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-border-standard bg-surface-container-low text-secondary">
              <ShieldAlert className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>
                Re-enter your platform owner password. A recent session is not
                enough — every privileged database control call is
                reauthenticated.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form
          className="space-y-4"
          data-testid="reauth-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            const nextPassword = String(data.get("current_password") ?? "");
            const nextConfirmation = String(data.get("confirmation") ?? "");
            if (!nextPassword) return;
            if (requiresUUID && nextConfirmation !== projectId) return;
            onConfirm(nextPassword, nextConfirmation);
          }}
        >
          {requiresUUID ? (
            <div>
              <label className="label" htmlFor="db-confirm-uuid">
                Type exact Project UUID *
              </label>
              <input
                id="db-confirm-uuid"
                name="confirmation"
                className="input font-mono text-sm"
                data-testid="reauth-confirmation"
                autoComplete="off"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder={projectId}
              />
            </div>
          ) : null}
          <div>
            <label className="label" htmlFor="db-reauth-password">
              Current owner password *
            </label>
            <input
              id="db-reauth-password"
              name="current_password"
              className="input w-full"
              data-testid="reauth-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={onCancel}
              data-testid="reauth-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit}
              data-testid="reauth-submit"
            >
              {busy ? "Submitting…" : "Confirm with password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CatalogOperations({
  catalog,
  busy,
  onQueue,
}: {
  catalog: Schema | null;
  busy: boolean;
  onQueue: (body: Record<string, unknown>, label: string) => void;
}) {
  const [kind, setKind] = useState("install_extension");
  const [name, setName] = useState("pg_trgm");
  const [value, setValue] = useState("");

  const submit = () => {
    const body: Record<string, unknown> = {
      kind,
      idempotency_key: `dashboard-${kind}-${crypto.randomUUID()}`,
      reason: `Dashboard ${kind}`,
    };
    if (kind === "install_extension") body.extension = { name };
    else if (kind.includes("role")) body.role = { name };
    else if (kind === "update_setting") body.setting = { name, value };
    else
      body.publication = {
        name,
        tables: value
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      };
    onQueue(body, formatOperationKind(kind));
  };

  return (
    <Card data-testid="section-catalog">
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-sm border border-border-standard bg-surface-container-low text-secondary">
            <ShieldAlert className="h-4 w-4" aria-hidden />
          </div>
          <div>
            <CardTitle>Controlled catalog operations</CardTitle>
            <CardDescription>
              Allowlisted extensions, restricted NOLOGIN roles, safe database
              settings and scoped publications. Tenant superuser is never
              granted. Queueing always asks for your password.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4"
          role="group"
          aria-label="Catalog operation form"
        >
          <select
            className="input"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            aria-label="Operation kind"
          >
            <option value="install_extension">Install extension</option>
            <option value="create_role">Create restricted role</option>
            <option value="drop_role">Drop restricted role</option>
            <option value="update_setting">Safe setting</option>
            <option value="create_publication">Create publication</option>
            <option value="drop_publication">Drop publication</option>
          </select>
          <input
            className="input"
            aria-label="Operation name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="input"
            aria-label="Value or comma-separated tables"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="value / tables"
          />
          <Button
            type="button"
            size="sm"
            disabled={busy || !name}
            onClick={submit}
          >
            Queue operation
          </Button>
        </div>
        <div className="grid gap-3 text-xs sm:grid-cols-3">
          <CatalogList
            title="Extensions"
            values={(catalog?.extensions ?? []).map(
              (v) => `${v.name} ${v.version}`,
            )}
          />
          <CatalogList
            title="Restricted roles"
            values={(catalog?.roles ?? [])
              .filter((v) => v.name.startsWith("project_role_"))
              .map(
                (v) =>
                  `${v.name}${v.can_login ? " · LOGIN" : " · NOLOGIN"}`,
              )}
          />
          <CatalogList
            title="Publications / settings"
            values={[
              ...(catalog?.publications ?? []).map((v) => v.name),
              ...(catalog?.settings ?? []).map(
                (v) => `${v.name}=${v.setting}`,
              ),
            ]}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function CatalogList({
  title,
  values,
}: {
  title: string;
  values: string[];
}) {
  return (
    <div className="rounded-md border border-border-standard bg-surface-container-low/40 p-3">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
        {title}
      </div>
      {values.length === 0 ? (
        <p className="text-text-muted">None</p>
      ) : (
        <ul className="space-y-0.5 font-mono text-text-primary">
          {values.map((v) => (
            <li key={v}>{v}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border-standard bg-surface-container-low/40 p-3">
      <div className="text-xs uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="mt-1 font-mono text-sm text-text-primary">{value}</div>
    </div>
  );
}

function budgetValue(
  profile: Record<string, unknown>,
  key: string,
  suffix: string,
): string {
  const value = profile[key];
  return typeof value === "number" ? `${value}${suffix}` : "—";
}

/** Scannable operation / history list: kind + state badge + detail. */
export function OperationHistory({
  title,
  empty = "No records.",
  rows,
  emphasize = false,
}: {
  title?: string;
  empty?: string;
  rows: HistoryRowModel[];
  emphasize?: boolean;
}) {
  if (!rows.length) {
    return (
      <div>
        {title ? (
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            {title}
          </div>
        ) : null}
        <p className="text-sm text-text-muted" data-testid="history-empty">
          {empty}
        </p>
      </div>
    );
  }

  return (
    <div>
      {title ? (
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          {title}
        </div>
      ) : null}
      <ul
        className={cn(
          "divide-y divide-border-standard rounded-md border border-border-standard",
          emphasize && "bg-surface-container-low/20",
        )}
        data-testid="history-list"
      >
        {rows.map((row) => (
          <li
            key={row.id}
            data-testid="operation-history-row"
            className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="font-mono text-sm text-text-primary">
                {formatOperationKind(row.kind)}
              </div>
              {row.detail ? (
                <div className="truncate text-xs text-text-muted">
                  {row.detail}
                </div>
              ) : null}
            </div>
            <Badge
              variant={stateBadgeVariant(row.state)}
              data-testid="operation-state-badge"
            >
              {row.state}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}
